import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, MessageCircle, Phone, MapPin } from "lucide-react";
import { CLINIC, bookingMessage, callLink, SERVICE_GROUPS } from "../data/clinic";
import { useLang } from "../i18n/LanguageContext";
import { TIME_SLOTS } from "../i18n/translations";
import { Reveal, Eyebrow } from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { toast } from "./ui/sonner";

export default function Contact() {
  const { lang, t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");

  const serviceNames = SERVICE_GROUPS.flatMap((g) => g.ids.map((id) => t.services.items[id].name));
  const timeSlots = TIME_SLOTS[lang] || TIME_SLOTS.en;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(c.validation);
      return;
    }
    const dateStr = date ? format(date, "dd MMM yyyy") : "";
    const datetime = [dateStr, time].filter(Boolean).join(lang === "hi" ? " को " : " at ");
    const url = bookingMessage({ ...form, datetime }, lang);
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(c.opening);
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-brand-bg py-20 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8">
        <Reveal>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-normal leading-tight tracking-tight text-brand-ink md:text-5xl">
            {c.heading}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-brand-inksoft">{c.intro}</p>

          <div className="mt-10 space-y-4">
            <a href={callLink} data-testid="contact-call-link" className="flex items-center gap-4 rounded-2xl border border-brand-border bg-brand-surface p-5 transition-colors hover:border-brand-primary">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary">
                <Phone className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-brand-inksoft">{c.callLabel}</span>
                <span className="font-display text-lg text-brand-ink">{CLINIC.phone}</span>
              </span>
            </a>
            <div className="flex items-center gap-4 rounded-2xl border border-brand-border bg-brand-surface p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-warm/10 text-brand-warm">
                <MapPin className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-brand-inksoft">{c.visitLabel}</span>
                <span className="text-sm font-medium text-brand-ink">{t.location.address}</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            data-testid="booking-form"
            className="rounded-3xl border border-brand-border bg-brand-surface p-6 shadow-xl shadow-brand-ink/5 md:p-8"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">{c.name}</Label>
                <Input id="name" data-testid="form-name" value={form.name} onChange={set("name")} placeholder={c.namePh} className="h-12 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{c.phone}</Label>
                <Input id="phone" data-testid="form-phone" value={form.phone} onChange={set("phone")} placeholder={c.phonePh} className="h-12 rounded-xl" />
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <Label>{c.service}</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="form-service" className="h-12 rounded-xl">
                  <SelectValue placeholder={c.servicePh} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {serviceNames.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{c.date}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="form-date"
                      className="flex h-12 w-full items-center gap-2 rounded-xl border border-input bg-transparent px-3 text-left text-sm text-brand-ink"
                    >
                      <CalendarIcon className="h-4 w-4 text-brand-inksoft" />
                      {date ? format(date, "dd MMM yyyy") : <span className="text-muted-foreground">{c.datePh}</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{c.time}</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="form-time" className="h-12 rounded-xl">
                    <SelectValue placeholder={c.timePh} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((tm) => (
                      <SelectItem key={tm} value={tm}>{tm}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <Label htmlFor="message">{c.message}</Label>
              <Textarea id="message" data-testid="form-message" value={form.message} onChange={set("message")} placeholder={c.messagePh} className="min-h-[96px] rounded-xl" />
            </div>

            <button
              type="submit"
              data-testid="form-submit-btn"
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-brand-warm px-7 py-4 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-warmHover"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
              {c.submit}
            </button>
            <p className="mt-3 text-center text-xs text-brand-inksoft">{c.note}</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
